import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, firstValueFrom, map, Observable, throwError} from 'rxjs';
import {ApiError} from '@core/types';
import {HTTP_STATUS, log} from '@core/utils';
import {AuthStateService} from '@auth/states';

type HttpOptions = {
  token?: string | null;
  headers?: HttpHeaders;
  json?: boolean;
  responseType?: 'json' | 'blob';
  skipAuthLogout?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  constructor(
    private http: HttpClient,
    private authState: AuthStateService) {
  }

  async get<T>(url: string, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const responseType = options?.responseType;
    return firstValueFrom<T>(this.http.get(url, {
      headers,
      ...(responseType ? {responseType} : {})
    } as any)
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  async post<T>(url: string, data?: any, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    return firstValueFrom<T>(this.http.post(url, data, {headers})
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  async put<T>(url: string, data?: any, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    return firstValueFrom<T>(this.http.put(url, data, {headers})
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  async patch<T>(url: string, data?: any, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    return firstValueFrom<T>(this.http.patch(url, data, {headers})
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  async delete<T>(url: string, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    return firstValueFrom<T>(this.http.delete(url, {headers})
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  async postForm<T>(url: string, data: FormData, options?: HttpOptions): Promise<T> {
    const headers = await this.getHeaders({...options, json: false});
    return firstValueFrom<T>(this.http.post(url, data, {headers})
      .pipe(
        map(response => response as T),
        catchError(this.handleError.bind(this, options))
      ));
  }

  private async getHeaders(options?: HttpOptions): Promise<HttpHeaders> {
    let headers = options?.headers ?? new HttpHeaders();

    if (options?.json !== false) {
      headers = headers.set('Content-Type', 'application/json');
    }

    const token = options?.token !== undefined ? options.token : this.authState.token();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private handleError(options: HttpOptions | undefined, error: HttpErrorResponse): Observable<never> {
    log.debug('Error en la solicitud HTTP:', error);

    if (error.status === HTTP_STATUS.UNAUTHORIZED && !options?.skipAuthLogout) {
      this.authState.logout();
    }

    let apiError: ApiError = {
      message: 'Ocurrió un error inesperado',
    }

    if (error.error instanceof ErrorEvent) {
      apiError.message = `Error: ${error.error.message}`;
      apiError.code = String(error.status || '');
    } else {
      const serverError = error.error || {};
      apiError = {
        message: serverError['hydra:description'] || serverError.message || `Error ${error.status}: ${error.message}`,
        details: serverError.violations || serverError.details || serverError,
      };
      apiError.code = String(serverError.code || error.status || '');
    }

    log.error('Error en la solicitud HTTP:', apiError);
    return throwError(() => apiError);
  }

  normalizeCollection<T>(response: any): {member: T[]; totalItems: number} {
    if (!response) {
      return {member: [], totalItems: 0};
    }

    if (Array.isArray(response)) {
      return {member: response as T[], totalItems: response.length};
    }

    const member = response['hydra:member'] || response.member || response.items || [];
    const totalItems = response['hydra:totalItems'] ?? response.totalItems ?? (Array.isArray(member) ? member.length : 0);

    return {member: Array.isArray(member) ? member : [], totalItems};
  }
}
